import React from 'react'
import { Card, Flex, Text } from '@tremor/react'
import { Trophy, Target, Zap, TrendingUp, CheckCircle2 } from '@/components/icons'
import type { BonusProgress as BonusProgressType } from '../types'
import { cn } from '@/lib/utils'

interface BonusProgressProps {
  bonus: BonusProgressType
}

interface StatBlockProps {
  label: string
  value: string | number
  icon: React.ReactNode
  highlight?: boolean
}

function StatBlock({ label, value, icon, highlight = false }: StatBlockProps) {
  return (
    <div className={cn(
      "px-4 py-3 rounded-lg transition-all duration-200",
      highlight
        ? "bg-amber-50 border border-amber-200 shadow-sm"
        : "bg-zinc-50 border border-zinc-100 hover:border-zinc-200"
    )}>
      <Flex alignItems="center" className="gap-2 mb-1">
        <span className={cn(
          highlight ? "text-amber-600" : "text-zinc-400"
        )}>{icon}</span>
        <Text className={cn(
          "text-xs uppercase tracking-wide font-medium",
          highlight ? "text-amber-700" : "text-zinc-500"
        )}>{label}</Text>
      </Flex>
      <span className={cn(
        "font-mono text-2xl font-bold",
        highlight ? "text-amber-700" : "text-zinc-900"
      )}>
        {value}
      </span>
    </div>
  )
}

export function BonusProgressV2({ bonus }: BonusProgressProps) {
  const isUnlocked = bonus.unlocked
  const progressPercent = bonus.progressPercentage

  return (
    <Card
      className={cn(
        "relative overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200 animate-fade-in",
        "border-l-4",
        isUnlocked
          ? "border-l-emerald-500 bg-gradient-to-br from-emerald-50 via-white to-white border-t border-r border-b border-emerald-100"
          : "border-l-violet-500 bg-gradient-to-br from-violet-50/50 via-white to-white border-t border-r border-b border-violet-100"
      )}
    >

      <div className="relative z-10">
        {/* Header */}
        <Flex justifyContent="between" alignItems="start">
          <div>
            <Flex alignItems="center" className="gap-2 mb-2">
              <Trophy className={cn(
                "w-6 h-6",
                isUnlocked ? "text-emerald-500" : "text-violet-500"
              )} />
              <Text className="text-zinc-500 uppercase tracking-wider text-xs font-semibold">
                Team Bonus Status
              </Text>
            </Flex>

            <Flex alignItems="baseline" className="gap-3">
              <span className={cn(
                "font-mono text-5xl font-black tracking-tight",
                isUnlocked ? "text-emerald-600" : "text-zinc-900"
              )}>
                {bonus.currentRate.toFixed(1)}%
              </span>
              <span className="text-zinc-400 text-lg">
                / {bonus.targetRate}% target
              </span>
            </Flex>
          </div>

          {/* Status Badge */}
          <div className={cn(
            "px-4 py-2 rounded-full font-mono text-sm font-bold uppercase tracking-wider shadow-sm",
            isUnlocked
              ? "bg-emerald-500 text-white"
              : "bg-violet-100 text-violet-700 border border-violet-200"
          )}>
            {isUnlocked ? (
              <Flex alignItems="center" className="gap-2">
                <Zap className="w-4 h-4" />
                BONUS UNLOCKED
              </Flex>
            ) : (
              <Flex alignItems="center" className="gap-2">
                <Target className="w-4 h-4" />
                IN PROGRESS
              </Flex>
            )}
          </div>
        </Flex>

        {/* Progress Bar - Clean Style */}
        <div className="mt-6 mb-4">
          <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
            {/* Progress Fill */}
            <div
              className={cn(
                "h-full rounded-full transition-all duration-700 ease-out",
                isUnlocked
                  ? "bg-emerald-500"
                  : "bg-violet-500"
              )}
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
          </div>
          {/* Progress Labels */}
          <Flex justifyContent="between" className="mt-2">
            <Text className="text-xs text-zinc-400">0%</Text>
            <Text className="text-xs text-zinc-400 font-medium">Target: {bonus.targetRate}%</Text>
            <Text className="text-xs text-zinc-400">100%</Text>
          </Flex>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <StatBlock
            label="Booked"
            value={bonus.booked}
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
          <StatBlock
            label="Opportunities"
            value={bonus.opportunities}
            icon={<Target className="w-4 h-4" />}
          />
          <StatBlock
            label="Need More"
            value={bonus.bookingsNeeded > 0 ? bonus.bookingsNeeded : '—'}
            icon={<TrendingUp className="w-4 h-4" />}
            highlight={bonus.bookingsNeeded > 0}
          />
        </div>
      </div>
    </Card>
  )
}
